(() => {
  'use strict';
  const root = document.currentScript;
  if (!root?.hasAttribute('data-clean-commerce-root') || window['clean-commerce']) return;
  const query = new URLSearchParams(location.search);
  if (query.get('clean_analytics') === 'off' || query.get('perf_test') === '1') return;
  const currency = root.dataset.cleanCommerceCurrency;
  const owners = new Set(['clean-product', 'clean-product-cart', 'clean-cart-page', 'legacy-product-form', 'legacy-cart', 'legacy-cart-drawer',
    'legacy-addons', 'legacy-family-addon', 'legacy-hamper', 'legacy-customizer']);
  const queue = [], seen = new Set();
  const stats = { queued: 0, sent: 0, dropped: 0, invalid: 0 };
  let bytes = 0, choice;
  const allowed = () => {
    if (typeof choice === 'boolean') return choice;
    try { return window.Shopify?.customerPrivacy?.currentVisitorConsent?.().analytics !== 'no'; }
    catch { return true; }
  };
  const id = value => /^\d{1,20}$/.test(String(value || '')) ? String(value) : null;
  const quantity = value => Number.isSafeInteger(Number(value)) && Number(value) > 0 && Number(value) <= 100000 ? Number(value) : null;
  const money = value => value !== null && value !== undefined && value !== '' && Number.isFinite(Number(value)) && Number(value) >= 0 && Number(value) <= 1e12 ? Number(value) : null;
  const nativeCurrency = value => /^[A-Z]{3}$/.test(value || '') ? value : null;
  const operation = () => crypto.randomUUID();
  // Snapshots are private, normalized values. No token, properties, title,
  // personalization, customer data or URLs enter the queue or data layer.
  const snapshot = cart => {
    if (!Array.isArray(cart?.items) || cart.items.length > 50 || cart.errors || cart.status) return null;
    const code = nativeCurrency((typeof cart.currency === 'object' ? cart.currency?.iso_code : cart.currency) || currency);
    if (!code) return null;
    const map = new Map();
    for (const line of cart.items) {
      // Delivery-fee merchandise is reconciled internally, not a shopper add.
      if (String(line.product_id || line.product?.id) === '10059690017015' ||
          ['51050538893559', '51050538926327', '51256352473335', '51256354898167', '51256354930935', '51256354963703', '51256354996471', '51256355029239', '51256355062007'].includes(String(line.variant_id || line.id))) continue;
      const variant = id(line.variant_id || line.variant?.id || line.id), product = id(line.product_id || line.product?.id), q = quantity(line.quantity);
      const total = money(line.final_line_price ?? (Number(line.final_price ?? line.price) * Number(line.quantity)));
      if (!variant || !product || !q || total === null) return null;
      const previous = map.get(variant);
      map.set(variant, { item_id: variant, clean_product_id: product,
        quantity: q + (previous?.quantity || 0), cents: total + (previous?.cents || 0) });
    }
    return { currency: code, lines: [...map.values()] };
  };
  const items = snap => snap.lines.map(line => ({ item_id: line.item_id, clean_product_id: line.clean_product_id,
    price: line.cents / line.quantity / 100, quantity: line.quantity }));
  const drop = () => { const row = queue.shift(); if (row) { bytes -= row.bytes; stats.dropped++; } };
  const flush = () => {
    if (!allowed()) { while (queue.length) drop(); stats.queued = 0; return; }
    while (queue.length && Date.now() - queue[0].time > 120000) drop();
    if (window['clean-analytics']?.status === 'worker-ready' && window.cleanDataLayer) {
      while (queue.length) {
        const row = queue.shift(); bytes -= row.bytes;
        window.cleanDataLayer.push({ ecommerce: null });
        window.cleanDataLayer.push(row.event);
        stats.sent++;
      }
    }
    stats.queued = queue.length;
  };
  const emit = (name, snap, owner, action, eventId = operation()) => {
    if (!allowed()) return;
    if (!owners.has(owner) || !snap || !nativeCurrency(snap.currency) || !Array.isArray(snap.lines) || snap.lines.length > 50 ||
        snap.lines.some(line => !id(line.item_id) || !id(line.clean_product_id) || !quantity(line.quantity) || money(line.cents) === null)) {
      stats.invalid++; return;
    }
    if (typeof eventId !== 'string' || !/^[A-Za-z0-9:_-]{1,100}$/.test(eventId)) { stats.invalid++; return; }
    if (seen.has(eventId)) return;
    const event = { event: name, clean_schema_version: 1, clean_event_id: eventId,
      clean_occurred_at_ms: Date.now(),
      clean_owner: owner, clean_surface: owner.startsWith('legacy-') ? 'legacy-storefront' : 'clean-storefront',
      clean_action: action, ecommerce: { currency: snap.currency,
        value: snap.lines.reduce((sum, line) => sum + line.cents, 0) / 100, items: items(snap) } };
    const size = new TextEncoder().encode(JSON.stringify(event)).length;
    if (size > 32768) { stats.dropped++; return; }
    seen.add(eventId);
    if (seen.size > 100) seen.delete(seen.values().next().value);
    while (queue.length >= 25 || bytes + size > 32768) drop();
    queue.push({ event, bytes: size, time: Date.now() }); bytes += size;
    flush();
  };
  const viewCart = (cart, owner, eventId) => emit('clean-view-cart', snapshot(cart), owner, 'view', eventId);
  const added = (result, requested, owner, eventId) => {
    if (result?.errors || result?.status) return;
    const lines = Array.isArray(result?.items) ? result.items : result?.id ? [result] : null;
    if (!lines || !Array.isArray(requested) || requested.length > 50) return;
    const snap = snapshot({ items: lines, currency });
    if (!snap) return;
    const wanted = new Map();
    for (const line of requested) {
      const variant = id(line.id), q = quantity(line.quantity);
      if (!variant || !q) return;
      wanted.set(variant, (wanted.get(variant) || 0) + q);
    }
    const accepted = [];
    for (const [variant, q] of wanted) {
      const line = snap.lines.find(line => line.item_id === variant);
      // /add may return the merged line quantity. Report only the accepted
      // request's units; a readback after an ambiguous write is never accepted.
      if (!line || line.quantity < q) return;
      accepted.push({ ...line, quantity: q, cents: line.cents / line.quantity * q });
    }
    if (accepted.length) emit('clean-add-to-cart', { currency: snap.currency, lines: accepted }, owner, 'add', eventId);
  };
  const changed = (before, cart, owner, eventId = operation(), affected = []) => {
    const after = snapshot(cart);
    if (!before || !after || before.currency !== after.currency) return;
    const additions = [], removals = [];
    const variants = new Set(affected.map(id).filter(Boolean));
    for (const variant of variants) {
      const prior = before.lines.find(line => line.item_id === variant), next = after.lines.find(line => line.item_id === variant);
      const delta = (next?.quantity || 0) - (prior?.quantity || 0);
      if (!delta) continue;
      const line = delta > 0 ? next : prior, q = Math.abs(delta);
      (delta > 0 ? additions : removals).push({ ...line, quantity: q, cents: line.cents / line.quantity * q });
    }
    if (additions.length) emit('clean-add-to-cart', { currency: after.currency, lines: additions }, owner, 'quantity', `${eventId}:add`);
    if (removals.length) emit('clean-remove-from-cart', { currency: before.currency, lines: removals }, owner, 'quantity', `${eventId}:remove`);
  };
  const fromElement = element => {
    const lines = [...element.querySelectorAll('[data-clean-commerce-line]')];
    if (lines.length > 50) return null;
    return { currency, items: lines.map(node => ({ variant_id: node.dataset.cleanCommerceVariant,
      product_id: node.dataset.cleanCommerceProduct, quantity: node.dataset.cleanCommerceQuantity,
      final_line_price: node.dataset.cleanCommerceCents })) };
  };
  const viewProduct = element => {
    const data = element.querySelector('[data-clean-commerce-product]');
    if (!data) return;
    const snap = snapshot(JSON.parse(data.textContent));
    if (!snap || !snap.lines.length || element.dataset.cleanCommerceViewed === snap.lines[0].item_id) return;
    element.dataset.cleanCommerceViewed = snap.lines[0].item_id;
    emit('clean-view-item', snap, element.hasAttribute('data-clean-product') ? 'clean-product' : 'legacy-product-form', 'resolved-variant');
  };
  const resolved = (element, variant, product) => {
    const snap = snapshot({ currency, items: [{ variant_id: variant?.id, product_id: product, quantity: 1,
      final_line_price: variant?.selected_selling_plan_allocation?.price ?? variant?.price }] });
    if (!snap?.lines.length || element.dataset.cleanCommerceViewed === snap.lines[0].item_id) return;
    element.dataset.cleanCommerceViewed = snap.lines[0].item_id;
    emit('clean-view-item', snap, 'legacy-product-form', 'resolved-variant');
  };
  const checkout = (cart, owner, eventId) => emit('clean-checkout-intent', snapshot(cart), owner, 'intent', eventId);
  // Capture is optional and cannot interrupt a successful customer operation.
  const safe = callback => (...args) => { try { return callback(...args); } catch { stats.invalid++; } };
  window['clean-commerce'] = Object.freeze({ operation: safe(operation), snapshot: safe(snapshot), added: safe(added),
    changed: safe(changed), viewCart: safe(viewCart), viewProduct: safe(viewProduct), checkout: safe(checkout),
    fromElement: safe(fromElement), resolved: safe(resolved), stats });
  document.dispatchEvent(new CustomEvent('clean:commerce:ready'));
  document.addEventListener('pt0', () => queueMicrotask(() => { try { flush(); } catch { stats.invalid++; } }), { once: true });
  document.addEventListener('visitorConsentCollected', event => {
    if (typeof event.detail?.analyticsAllowed === 'boolean') choice = event.detail.analyticsAllowed;
    safe(flush)();
  });
  const initialize = safe(() => {
    document.querySelectorAll('[data-clean-product], [data-clean-commerce-product-root]').forEach(safe(viewProduct));
    const cart = document.querySelector('[data-clean-commerce-cart-page]');
    if (cart) viewCart(fromElement(cart), 'legacy-cart');
  });
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', initialize, { once: true });
  else initialize();
})();
